import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Grid, List, SlidersHorizontal } from 'lucide-react';
import { useTrucks } from '../hooks/useTrucks';
import { LoadingCard, LoadingPage } from '../components/LoadingSpinner';
import SEO from '../components/SEO';
import { SoldRibbon } from '../components/SoldRibbon';

const TrucksPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [mileageRange, setMileageRange] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(true);

  // Build filters for API call - always load all trucks by default
  const apiFilters = useMemo(() => {
    const filters = { limit: 50 }; // Load up to 50 trucks by default
    if (searchTerm) filters.search = searchTerm;
    if (selectedMake) filters.make = selectedMake;
    if (selectedYear) filters.year = parseInt(selectedYear);
    return filters;
  }, [searchTerm, selectedMake, selectedYear]);

  const { trucks, loading, error } = useTrucks(apiFilters);

  // Client-side filtering for price range and mileage (since API doesn't support them yet)
  const filteredTrucks = useMemo(() => {
    let filtered = trucks;
    
    // Filter by price range
    if (priceRange) {
      filtered = filtered.filter(truck => {
        const price = truck.price || 0;
        switch (priceRange) {
          case 'under30k':
            return price < 30000;
          case '30k-45k':
            return price >= 30000 && price <= 45000;
          case '45k-60k':
            return price > 45000 && price <= 60000;
          case 'over60k':
            return price > 60000;
          default:
            return true;
        }
      });
    }
    
    // Filter by mileage range
    if (mileageRange) {
      filtered = filtered.filter(truck => {
        const mileage = truck.mileage || 0;
        // Using km ranges directly (no conversion needed)
        switch (mileageRange) {
          case '0-50k':
            return mileage <= 50000; // 0-50,000 km
          case '50k-100k':
            return mileage > 50000 && mileage <= 100000; // 50k-100k km
          case '100k-200k':
            return mileage > 100000 && mileage <= 200000; // 100k-200k km
          case '200k+':
            return mileage > 200000; // 200,000+ km
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [trucks, priceRange, mileageRange]);

  // Get unique values for filters from loaded trucks
  const makes = [...new Set(trucks.map(truck => truck.make))].sort();
  const years = [...new Set(trucks.map(truck => truck.year))].sort((a, b) => b - a);

  if (loading && trucks.length === 0) {
    return <LoadingPage message="Loading trucks..." />;
  }

  const TruckCard = ({ truck, isListView = false }) => (
    <div className={`bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all group ${
      isListView ? 'flex' : ''
    }`}>
      <div className={`relative ${isListView ? 'w-1/3' : ''}`}>
        <img 
          src={truck.images[0]} 
          alt={truck.title}
          className={`object-cover group-hover:scale-105 transition-transform duration-300 ${
            isListView ? 'w-full h-full' : 'w-full h-48'
          } ${truck.is_sold ? 'grayscale opacity-80' : ''}`}
        />
        {truck.is_sold && <SoldRibbon size="md" />}
        {truck.available_for_hire && (
          <div className="absolute top-4 left-4">
            <div className="bg-green-500/80 backdrop-blur-sm border border-green-400/30 text-white px-3 py-1 rounded-lg text-sm font-bold shadow-xl">
              Hire Fleet
            </div>
          </div>
        )}
      </div>
      <div className={`p-6 ${isListView ? 'flex-1' : ''}`}>
        <h3 className="text-xl font-bold text-black mb-2 font-inter line-clamp-2">{truck.title}</h3>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span>{truck.year}</span>
          <span>•</span>
          <span>{truck.mileage ? truck.mileage.toLocaleString() : '0'} km</span>
          <span>•</span>
          <span>{truck.emissions}</span>
        </div>
        <p className="text-gray-600 mb-4 line-clamp-2 font-inter">{truck.description}</p>
        
        {isListView && (
          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div><strong>Power:</strong> {truck.power}</div>
            <div><strong>Weight:</strong> {truck.weight}t</div>
            <div><strong>Axle:</strong> {truck.axle}</div>
            <div><strong>Cab Type:</strong> {truck.cabtype}</div>
          </div>
        )}
        
        <div className="flex justify-between items-center">
          <div className="text-2xl font-bold text-black">
            {truck.is_sold ? <span className="text-red-600">SOLD</span> : (truck.price ? `£${truck.price.toLocaleString()}` : 'POA')}
          </div>
          <Link 
            to={`/trucks/${truck.id}`}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors"
          >
            View Details
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="All Trucks for Sale"
        description="Browse our extensive collection of quality MAN trucks for sale in Leicester. Chassis cabs, rigid trucks, tractor units, curtainsiders, and box vans. Professional inspection and up to 12 months coverage."
        keywords="MAN trucks for sale Leicester, commercial trucks, chassis cabs, rigid trucks, tractor units, curtainsiders, box vans"
        canonical="/trucks"
      />
      
      {/* Header */}
      <div className="bg-black text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl lg:text-4xl font-black font-inter">
            Browse Our <span className="text-orange-500">Trucks</span>
          </h1>
          <p className="text-gray-300 mt-2 font-inter">
            Find the perfect commercial vehicle for your business needs.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          {/* Search Bar */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Search Our Inventory</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search by make, model, registration, or stock number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded transition-colors"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 font-inter">View:</span>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Manufacturer</label>
                <select
                  value={selectedMake}
                  onChange={(e) => setSelectedMake(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">All Manufacturers</option>
                  {makes.map(make => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Model Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">Any Model Year</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Mileage Range</label>
                <select
                  value={mileageRange}
                  onChange={(e) => setMileageRange(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">Any Mileage</option>
                  <option value="0-50k">0 - 50,000 km</option>
                  <option value="50k-100k">50,000 - 100,000 km</option>
                  <option value="100k-200k">100,000 - 200,000 km</option>
                  <option value="200k+">200,000+ km</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Budget Range</label>
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent font-inter"
                >
                  <option value="">Any Budget</option>
                  <option value="under30k">Under £30,000</option>
                  <option value="30k-45k">£30,000 - £45,000</option>
                  <option value="45k-60k">£45,000 - £60,000</option>
                  <option value="over60k">Over £60,000</option>
                </select>
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedMake('');
                    setSelectedYear('');
                    setPriceRange('');
                    setMileageRange('');
                  }}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded font-bold transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600 font-inter">
            Showing {filteredTrucks.length} truck{filteredTrucks.length !== 1 ? 's' : ''}
            {loading && ' (loading...)'}
          </p>
          {error && (
            <p className="text-red-600 font-inter mt-2">{error}</p>
          )}
        </div>

        {/* Trucks Grid/List */}
        <div className={
          viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
            : 'space-y-6'
        }>
          {loading && filteredTrucks.length === 0 ? (
            // Loading placeholders
            Array.from({ length: 6 }).map((_, index) => (
              <LoadingCard key={index} />
            ))
          ) : (
            filteredTrucks.map((truck) => (
              <TruckCard key={truck.id} truck={truck} isListView={viewMode === 'list'} />
            ))
          )}
        </div>

        {!loading && filteredTrucks.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">🚛</div>
            <h3 className="text-xl font-bold text-gray-700 mb-2 font-inter">No trucks found</h3>
            <p className="text-gray-600 font-inter">Try adjusting your search criteria or filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrucksPage;